import { useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { effectiveAccess, type World } from "../sendItEngine";
import { FILE_META, FILE_ORDER, fileOf, personOf, placeLabel, placeShort, type FileId } from "./content";
import { Avatar, FileGlyph, Ic } from "./icons";
import { tx, txr } from "../../lib/tx";

interface MenuState {
  x: number;
  y: number;
  id: FileId;
}

/** Windows Explorer style window for the workshop files: command bar, details list, preview pane. */
export function FilesWin({
  world,
  selected,
  hotFileId,
  hotShare,
  taskFileId,
  doneFileIds,
  onSelect,
  onOpen,
  onShare,
  onManage,
}: {
  world: World;
  selected: FileId | null;
  hotFileId?: FileId;
  hotShare?: boolean;
  taskFileId?: FileId;
  doneFileIds?: FileId[];
  onSelect: (id: FileId) => void;
  onOpen: (id: FileId) => void;
  onShare: (id: FileId) => void;
  onManage: (id: FileId) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const seenPlace = useRef<Record<string, string>>({});

  // When the owner moves a file, its Location cell flashes so the change is noticed.
  useEffect(() => {
    let moved: string | null = null;
    for (const id of FILE_ORDER) {
      const now = placeLabel(world, id);
      const before = seenPlace.current[id];
      if (before !== undefined && before !== now) moved = id;
      seenPlace.current[id] = now;
    }
    if (!moved) return;
    setFlash(moved);
    const t = window.setTimeout(() => setFlash(null), 1400);
    return () => window.clearTimeout(t);
  }, [world]);

  useEffect(() => {
    if (!menu) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest(".sis-ctx")) setMenu(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setMenu(null);
      }
    };
    window.addEventListener("pointerdown", away, true);
    window.addEventListener("keydown", esc, true);
    return () => {
      window.removeEventListener("pointerdown", away, true);
      window.removeEventListener("keydown", esc, true);
    };
  }, [menu]);

  const openMenu = (e: ReactMouseEvent, id: FileId) => {
    e.preventDefault();
    onSelect(id);
    const r = rootRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = Math.min(e.clientX - r.left, r.width - 176);
    const y = Math.min(e.clientY - r.top, r.height - 124);
    setMenu({ x: Math.max(4, x), y: Math.max(4, y), id });
  };

  const rows = FILE_ORDER.map((id) => {
    const acc = effectiveAccess(world, id);
    return {
      id,
      f: fileOf(id),
      meta: FILE_META[id],
      place: placeLabel(world, id),
      placeShort: placeShort(world, id),
      acc,
      link: acc.some((a) => a.personId === "link-holder"),
      people: acc.filter((a) => a.personId !== "link-holder"),
    };
  });
  const sel = selected ? rows.find((r) => r.id === selected) : undefined;

  const act = (fn: (id: FileId) => void) => () => {
    if (selected) fn(selected);
  };

  return (
    <div className="sis-fx" ref={rootRef}>
      <div className="sis-fx-addr">
        <span className="sis-fx-nav" aria-hidden="true"><Ic n="back" size={14} /></span>
        <span className="sis-fx-nav is-dim" aria-hidden="true"><Ic n="fwd" size={14} /></span>
        <span className="sis-fx-nav" aria-hidden="true"><Ic n="up" size={14} /></span>
        <span className="sis-fx-path">
          <Ic n="folder" size={13} /> {tx("This PC")} <Ic n="chevron" size={11} /> {tx("Documents")} <Ic n="chevron" size={11} /> <b>{tx("Workshop")}</b>
        </span>
        <span className="sis-fx-find"><Ic n="search" size={13} /> {tx("Search Workshop")}</span>
      </div>

      <div className="sis-fx-cmd">
        <button type="button" className="sis-cmd" disabled={!selected} onClick={act(onOpen)}>
          <Ic n="doc" size={15} /> {tx("Open")}
        </button>
        <button type="button" className={`sis-cmd sis-cmd--share${hotShare ? " hot" : ""}`} disabled={!selected} onClick={act(onShare)}>
          <Ic n="share" size={15} /> {tx("Share")}
        </button>
        <button type="button" className="sis-cmd" disabled={!selected} onClick={act(onManage)}>
          <Ic n="lock" size={15} /> {tx("Manage access")}
        </button>
        <span className="sis-cmd-note">{selected ? "" : tx("Select a file to see what you can do with it")}</span>
      </div>

      <div className="sis-fx-body">
        <nav className="sis-fx-side" aria-label={tx("Folders")}>
          <div className="sis-fx-sgrp">{tx("Quick access")}</div>
          <div className="sis-fx-sitem"><Ic n="doc" size={14} /> {tx("Desktop")}</div>
          <div className="sis-fx-sitem"><Ic n="download" size={14} /> {tx("Downloads")}</div>
          <div className="sis-fx-sgrp">{tx("This PC")}</div>
          <div className="sis-fx-sitem"><Ic n="folder" size={14} /> {tx("Documents")}</div>
          <div className="sis-fx-sitem is-on"><Ic n="folder" size={14} /> {tx("Workshop")}</div>
        </nav>

        <div className="sis-fx-list">
          <div className="sis-fx-head" aria-hidden="true">
            <span>{tx("Name")}</span>
            <span className="sis-fx-mod">{tx("Date modified")}</span>
            <span className="sis-fx-type">{tx("Type")}</span>
            <span className="sis-fx-loc">{tx("Location")}</span>
          </div>
          <div className="sis-fx-rows sis-scroll" role="list">
            {rows.map((r, i) => {
              const isTask = taskFileId === r.id;
              const isDone = !!doneFileIds?.includes(r.id) && !isTask;
              return (
              <button
                key={r.id}
                type="button"
                role="listitem"
                className={`sis-fx-row${selected === r.id ? " is-sel" : ""}${hotFileId === r.id || isTask ? " hot" : ""}${isDone ? " is-done" : ""}${isTask ? " is-task" : ""}`}
                style={{ "--i": i } as CSSProperties}
                onClick={() => onSelect(r.id)}
                onDoubleClick={() => onOpen(r.id)}
                onContextMenu={(e) => openMenu(e, r.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onOpen(r.id);
                  }
                }}
              >
                <span className="sis-fx-name">
                  <FileGlyph ext={r.meta.ext} size={24} />
                  <span className="sis-fx-nm">{r.f.name}</span>
                  {isTask && <span className="sis-fx-pill is-task">{tx("This task")}</span>}
                  {isDone && <span className="sis-fx-pill is-done">{tx("Shared")}</span>}
                  {!isTask && !isDone && r.acc.length > 0 && (
                    <span className="sis-fx-badge" title={tx("Shared")}>
                      <Ic n={r.link ? "link" : "users"} size={11} /> {r.acc.length}
                    </span>
                  )}
                </span>
                <span className="sis-fx-mod">{r.meta.modified}</span>
                <span className="sis-fx-type">{r.meta.type}</span>
                <span className={`sis-fx-loc${flash === r.id ? " is-flash" : ""}`} title={r.place}>{r.placeShort}</span>
              </button>
              );
            })}
          </div>
          <div className="sis-fx-stat">
            {txr("{length} items{v}<0>Double-click a file to open it. Right-click for more.</0>", { length: rows.length, v: selected ? tx(" · 1 selected") : "" }, [<span className="sis-fx-tip" />])}
          </div>
        </div>

        <aside className="sis-fx-prev" aria-label={tx("Preview")}>
          {sel ? (
            <div className="sis-fx-previn" key={sel.id}>
              <div className="sis-fx-phead">
                <FileGlyph ext={sel.meta.ext} size={40} />
                <div>
                  <strong>{sel.f.name}</strong>
                  <span>{sel.meta.type}</span>
                </div>
              </div>
              <p className="sis-fx-sum">{sel.f.summary}</p>
              <div className="sis-fx-paper">
                {sel.f.preview.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
              <dl className="sis-fx-facts">
                <dt>{tx("Location")}</dt>
                <dd>{sel.place}</dd>
                <dt>{tx("Shared with")}</dt>
                <dd>
                  {sel.acc.length === 0 ? (
                    <span className="sis-dim">{tx("Only you")}</span>
                  ) : (
                    <span className="sis-fx-who">
                      {sel.people.slice(0, 3).map((p) => {
                        const per = personOf(p.personId);
                        return per ? <Avatar key={`${p.personId}-${p.via}`} name={per.name} org={per.org} size={20} /> : null;
                      })}
                      {sel.link && (
                        <span className="sis-fx-linkchip"><Ic n="link" size={11} /> {tx("Anyone with the link")}</span>
                      )}
                      <span className="sis-dim">
                        {sel.people.length > 0 ? `${sel.people.length} ${sel.people.length === 1 ? "person" : "people"}` : ""}
                      </span>
                    </span>
                  )}
                </dd>
              </dl>
              <div className="sis-fx-pbtn">
                <button type="button" className="sis-btn sis-btn--primary" onClick={() => onShare(sel.id)}>
                  <Ic n="share" size={14} /> {tx("Share")}
                </button>
                <button type="button" className="sis-btn" onClick={() => onManage(sel.id)}>
                  <Ic n="lock" size={14} /> {tx("Manage access")}
                </button>
              </div>
            </div>
          ) : (
            <div className="sis-fx-empty">
              <Ic n="doc" size={30} />
              <strong>{tx("Select a file to preview it")}</strong>
              <span>{tx("You can read what is inside before you share it.")}</span>
            </div>
          )}
        </aside>
      </div>

      {menu && (
        <div className="sis-ctx" style={{ left: menu.x, top: menu.y }} role="menu">
          <button type="button" role="menuitem" onClick={() => { setMenu(null); onOpen(menu.id); }}>
            <Ic n="doc" size={14} /> {tx("Open")}
          </button>
          <button type="button" role="menuitem" onClick={() => { setMenu(null); onShare(menu.id); }}>
            <Ic n="share" size={14} /> {tx("Share")}
          </button>
          <button type="button" role="menuitem" onClick={() => { setMenu(null); onManage(menu.id); }}>
            <Ic n="lock" size={14} /> {tx("Manage access")}
          </button>
        </div>
      )}
    </div>
  );
}
